import React, { useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { t } from '../data/translations';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { MapPin, Mail, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { lang } = useLang();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    message: '',
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success[lang]);
      setForm({ first_name: '', last_name: '', company_name: '', message: '' });
    } catch {
      toast.error(t.contact.error[lang]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left - Info */}
          <div>
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB] font-['Manrope'] mb-4">
              {t.contact.overline[lang]}
            </span>
            <h2
              data-testid="contact-title"
              className="font-['Outfit'] text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A] leading-tight mb-4"
            >
              {t.contact.title[lang]}
            </h2>
            <p className="font-['Manrope'] text-base md:text-lg text-[#475569] leading-relaxed mb-12">
              {t.contact.subtitle[lang]}
            </p>

            {/* Company info */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center border border-[#E2E8F0] flex-shrink-0">
                  <MapPin className="w-4 h-4 text-[#334155]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-['Manrope'] text-sm font-semibold text-[#0F172A] mb-1">
                    {t.contact.companyInfo}
                  </p>
                  <p className="font-['Manrope'] text-sm text-[#475569] whitespace-pre-line">
                    {t.contact.address}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center border border-[#E2E8F0] flex-shrink-0">
                  <Mail className="w-4 h-4 text-[#334155]" strokeWidth={1.5} />
                </div>
                <div>
                  <a href={`mailto:${t.contact.email}`} className="font-['Manrope'] text-sm text-[#475569] hover:text-[#2563EB] transition-colors">
                    {t.contact.email}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div className="bg-white border border-[#E2E8F0] p-8 md:p-10">
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block font-['Manrope'] text-xs font-semibold text-[#1E293B] uppercase tracking-wider mb-2">
                    {t.contact.firstName[lang]}
                  </label>
                  <Input
                    data-testid="contact-firstname"
                    name="first_name"
                    value={form.first_name}
                    onChange={handleChange}
                    required
                    className="rounded-none bg-[#F8FAFC] border-[#E2E8F0] focus-visible:ring-1 focus-visible:ring-[#2563EB] h-11 font-['Manrope'] text-sm"
                    placeholder={t.contact.firstName[lang]}
                  />
                </div>
                <div>
                  <label className="block font-['Manrope'] text-xs font-semibold text-[#1E293B] uppercase tracking-wider mb-2">
                    {t.contact.lastName[lang]}
                  </label>
                  <Input
                    data-testid="contact-lastname"
                    name="last_name"
                    value={form.last_name}
                    onChange={handleChange}
                    required
                    className="rounded-none bg-[#F8FAFC] border-[#E2E8F0] focus-visible:ring-1 focus-visible:ring-[#2563EB] h-11 font-['Manrope'] text-sm"
                    placeholder={t.contact.lastName[lang]}
                  />
                </div>
              </div>

              <div>
                <label className="block font-['Manrope'] text-xs font-semibold text-[#1E293B] uppercase tracking-wider mb-2">
                  {t.contact.companyName[lang]}
                </label>
                <Input
                  data-testid="contact-company"
                  name="company_name"
                  value={form.company_name}
                  onChange={handleChange}
                  required
                  className="rounded-none bg-[#F8FAFC] border-[#E2E8F0] focus-visible:ring-1 focus-visible:ring-[#2563EB] h-11 font-['Manrope'] text-sm"
                  placeholder={t.contact.companyName[lang]}
                />
              </div>

              <div>
                <label className="block font-['Manrope'] text-xs font-semibold text-[#1E293B] uppercase tracking-wider mb-2">
                  {t.contact.message[lang]}
                </label>
                <Textarea
                  data-testid="contact-message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="rounded-none bg-[#F8FAFC] border-[#E2E8F0] focus-visible:ring-1 focus-visible:ring-[#2563EB] font-['Manrope'] text-sm resize-none"
                  placeholder={t.contact.message[lang]}
                />
              </div>

              <Button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={loading}
                className="w-full bg-[#0F172A] text-white hover:bg-[#2563EB] rounded-none font-['Manrope'] text-sm font-semibold h-12 transition-colors duration-200"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    {t.contact.submit[lang]}
                    <Send className="w-4 h-4 ml-2" strokeWidth={1.5} />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
